import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Paperclip, Send, Trash2, Wand2, Palette, Sparkles, Save, PlusCircle, Code, Eye } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { emailApi } from '../services/emailApi';
import { aiApi } from '../services/aiApi';
import { templateApi } from '../services/templateApi';
import { EmailCredentials, EmailData } from '../types/email';
import { OAuthTokens, UserInfo } from '../services/oauthApi';

import toast from 'react-hot-toast';

const EmailForm: React.FC = () => {
  const location = useLocation();
  const { userId } = useAuth();
  const [credentials, setCredentials] = useState<EmailCredentials>({
    email: '',
    accessToken: undefined,
  });
  const [emailData, setEmailData] = useState<EmailData>({
    to: '',
    subject: '',
    html: '',
    attachments: [],
  });
  const [plainText, setPlainText] = useState('');
  const [tweakDescription, setTweakDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isTweaking, setIsTweaking] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState<any>(null);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const [, forceUpdate] = useState(0);
  const [editableHtmlContent, setEditableHtmlContent] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [editMode, setEditMode] = useState<'preview' | 'code'>('preview');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewDivRef = useRef<HTMLDivElement>(null);

  // Sync with localStorage (DashboardLayout handles the updates)
  useEffect(() => {
    const loadOAuthData = () => {
      try {
        const tokensStr = localStorage.getItem('oauth_tokens');
        const userInfoStr = localStorage.getItem('oauth_user_info');
        if (tokensStr && userInfoStr) {
          const tokens: OAuthTokens = JSON.parse(tokensStr);
          const info: UserInfo = JSON.parse(userInfoStr);
          setCredentials({
            email: info.email,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token || undefined,
          });
        } else {
          setCredentials({ email: '', accessToken: undefined });
        }
      } catch (error) {
        console.error('Error loading OAuth data:', error);
      }
    };

    loadOAuthData();
    window.addEventListener('storage', loadOAuthData);
    const interval = setInterval(loadOAuthData, 2000);
    return () => {
      window.removeEventListener('storage', loadOAuthData);
      clearInterval(interval);
    };
  }, []);

  const isAnyLoading = isConverting || isTweaking || isLoading;
  const loadingElapsed = loadingStartTime ? Date.now() - loadingStartTime : 0;
  const showExtendedMessage = loadingElapsed > 10000;

  useEffect(() => {
    if (isAnyLoading && !loadingStartTime) {
      setLoadingStartTime(Date.now());
    } else if (!isAnyLoading && loadingStartTime) {
      setLoadingStartTime(null);
    }
  }, [isAnyLoading, loadingStartTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAnyLoading) {
      interval = setInterval(() => forceUpdate(prev => prev + 1), 1000);
    }
    return () => interval && clearInterval(interval);
  }, [isAnyLoading]);

  useEffect(() => {
    if (emailData.html !== editableHtmlContent) {
      setEditableHtmlContent(emailData.html || '');
    }
    if (editMode === 'preview' && previewDivRef.current && previewDivRef.current.innerHTML !== emailData.html) {
      previewDivRef.current.innerHTML = emailData.html || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailData.html, editMode]);

  useEffect(() => {
    if (location.state?.selectedTemplate) {
      const template = location.state.selectedTemplate;
      setEmailData(prev => ({ ...prev, subject: template.subject, html: template.htmlContent }));
      setPlainText(template.plainText || '');
      toast.success(`Loaded: ${template.name}`);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleEmailDataChange = (field: keyof Omit<EmailData, 'attachments'>, value: string) => {
    setEmailData(prev => ({ ...prev, [field]: value }));
  };

  const handlePreviewInput = (e: React.FormEvent<HTMLDivElement>) => {
    setEditableHtmlContent(e.currentTarget.innerHTML);
  };

  const handlePreviewBlur = () => {
    if (editableHtmlContent !== emailData.html) {
      handleEmailDataChange('html', editableHtmlContent);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    processFiles(files);
  };

  const processFiles = (files: File[]) => {
    // Basic validation: filter out large files if needed
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB`);
        return false;
      }
      return true;
    });

    setEmailData(prev => ({ ...prev, attachments: [...prev.attachments, ...validFiles] }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const removeAttachment = (index: number) => {
    setEmailData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const convertToHtml = async () => {
    if (!plainText.trim()) return toast.error('Enter text to convert');
    setIsConverting(true);
    try {
      const result = await aiApi.convertToHtml({ plainText: plainText.trim() });
      if (result.success && result.htmlContent) {
        setEmailData(prev => ({ ...prev, html: result.htmlContent! }));
        toast.success('Converted!');
      } else toast.error(result.message || 'Conversion failed');
    } catch (error) {
      toast.error('Conversion failed');
    } finally {
      setIsConverting(false);
    }
  };

  const tweakHtml = async () => {
    if (!tweakDescription.trim() || !emailData.html) return toast.error('Describe changes');
    setIsTweaking(true);
    try {
      const result = await aiApi.tweakHtml({ originalHtml: emailData.html, tweakDescription: tweakDescription.trim() });
      if (result.success && result.htmlContent) {
        setEmailData(prev => ({ ...prev, html: result.htmlContent! }));
        toast.success('Tweaked!');
        setTweakDescription('');
      } else toast.error(result.message || 'Tweak failed');
    } catch (error) {
      toast.error('Tweak failed');
    } finally {
      setIsTweaking(false);
    }
  };

  const sendEmail = async () => {
    if (!credentials.email || !credentials.accessToken) return toast.error('Connect Gmail in the sidebar first');
    if (!emailData.to || !emailData.subject || (!emailData.html && !plainText.trim())) return toast.error('Fill required fields');
    setPendingEmailData({ credentials, emailData, plainText });
    setShowSaveTemplateModal(true);
  };

  const handleSendEmail = async (saveAsTemplate: boolean, templateName?: string) => {
    if (!pendingEmailData) return;
    const { credentials, emailData, plainText } = pendingEmailData;
    const finalEmailData = { ...emailData };
    if (!finalEmailData.html && plainText) finalEmailData.html = `<div>${plainText.replace(/\n/g, '<br>')}</div>`;

    if (saveAsTemplate && templateName?.trim() && userId) {
      try {
        await templateApi.createTemplate({ name: templateName.trim(), subject: emailData.subject, htmlContent: finalEmailData.html, plainText: plainText }, userId);
        toast.success('Template saved!');
      } catch (error) {
        toast.error('Template save failed, sending email anyway');
      }
    }

    setShowSaveTemplateModal(false);
     setIsLoading(true);
     try {
       const result = await emailApi.sendEmail(credentials, finalEmailData);
       if (result.success) {
         toast.success('Sent!');
         setEmailData({ to: '', subject: '', html: '', attachments: [] });
         setPlainText('');
         setTweakDescription('');
         if (fileInputRef.current) fileInputRef.current.value = '';
       } else toast.error(result.message || 'Send failed');
     } catch (error: any) {
       // Show specific error message from the error
       toast.error(error.message || 'Send failed');
     } finally {
       setIsLoading(false);
       setShowSaveTemplateModal(false);
       setPendingEmailData(null);
       setTemplateNameInput('');
     }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB'], i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-4xl mx-auto py-2">
      <div className="card relative overflow-hidden transition-all duration-300">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm z-10 flex items-center justify-center animate-in fade-in duration-300">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
              <p className="mt-4 font-semibold text-gray-900 dark:text-white">Relaying your message...</p>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <PlusCircle className="w-7 h-7 mr-3 text-blue-600" />
              Compose Message
            </h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the details to send a professional email</p>
          </div>
          <button
            onClick={() => setShowSaveTemplateModal(true)}
            className="btn-secondary text-sm flex items-center px-4"
            disabled={!emailData.subject && !emailData.html}
            title="Save for later"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Template
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); sendEmail(); }} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-text">Recipient Email</label>
              <input
                type="email"
                required
                className="input-field"
                placeholder="client@example.com"
                value={emailData.to}
                onChange={(e) => handleEmailDataChange('to', e.target.value)}
              />
            </div>
            <div>
              <label className="label-text">Subject Line</label>
              <input
                type="text"
                required
                className="input-field"
                placeholder="Project Update"
                value={emailData.subject}
                onChange={(e) => handleEmailDataChange('subject', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center text-purple-600 dark:text-purple-400 font-semibold mb-2">
              <Sparkles className="w-5 h-5 mr-2" />
              AI Magic Panel
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="label-text">Drafting thoughts (Plain Text)</label>
                <textarea
                  className="input-field"
                  rows={4}
                  placeholder="Type your message idea here..."
                  value={plainText}
                  onChange={(e) => setPlainText(e.target.value)}
                />
                <button
                  type="button"
                  onClick={convertToHtml}
                  disabled={isConverting || !plainText.trim()}
                  className="btn-primary mt-3 w-full sm:w-auto flex items-center justify-center"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {isConverting ? 'Polishing...' : 'Convert to HTML'}
                </button>
              </div>

              {emailData.html && (
                <div className="animate-in slide-in-from-top duration-500">
                  <div className="flex justify-between items-end mb-2">
                    <label className="label-text mb-0">Editor & Preview</label>
                    <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={() => setEditMode('preview')}
                        className={`flex items-center px-3 py-1 transparent text-xs font-medium rounded-md transition-all ${editMode === 'preview'
                          ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        Visual
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditMode('code')}
                        className={`flex items-center px-3 py-1 transparent text-xs font-medium rounded-md transition-all ${editMode === 'code'
                          ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                      >
                        <Code className="w-3.5 h-3.5 mr-1.5" />
                        HTML
                      </button>
                    </div>
                  </div>

                  {editMode === 'preview' ? (
                    <div
                      ref={previewDivRef}
                      contentEditable
                      onInput={handlePreviewInput}
                      onBlur={handlePreviewBlur}
                      className="input-field min-h-[300px] prose dark:prose-invert max-w-none focus:ring-purple-500/50 overflow-y-auto"
                    />
                  ) : (
                    <textarea
                      value={editableHtmlContent}
                      onChange={(e) => {
                        setEditableHtmlContent(e.target.value);
                        handleEmailDataChange('html', e.target.value);
                      }}
                      className="input-field min-h-[300px] font-mono text-sm focus:ring-blue-500/50"
                      placeholder="Paste your HTML here..."
                    />
                  )}

                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
                    <label className="label-text flex items-center">
                      <Palette className="w-4 h-4 mr-2" />
                      Request AI Tweak
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input-field flex-1"
                        placeholder="e.g. 'Make it sound more urgent'"
                        value={tweakDescription}
                        onChange={(e) => setTweakDescription(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={tweakHtml}
                        disabled={isTweaking || !tweakDescription.trim()}
                        className="btn-secondary px-6 shrink-0"
                      >
                        {isTweaking ? 'Tweaking...' : 'Apply'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
            <label className="label-text">Attachments</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition-all group ${isDragging
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10 scale-[1.01] shadow-inner'
                : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
            >
              <div className="space-y-1 text-center">
                <Paperclip className={`mx-auto h-12 w-12 transition-colors ${isDragging ? 'text-blue-500 scale-110' : 'text-gray-400 group-hover:text-blue-500'}`} />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label className="relative cursor-pointer bg-white dark:bg-gray-900 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus:outline-none">
                    <span>Upload files</span>
                    <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} className="sr-only" />
                  </label>
                  <p className="pl-1">{isDragging ? 'Drop to attach' : "drop it like it's hot"}</p>
                </div>
                <p className="text-xs text-gray-500">Up to 10MB per file</p>
              </div>
            </div>

            {emailData.attachments.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {emailData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50 group">
                    <div className="flex items-center min-w-0">
                      <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg mr-3">
                        <Paperclip className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                        <p className="text-xs text-blue-600 opacity-70">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button onClick={() => removeAttachment(index)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={sendEmail}
            disabled={isLoading}
            className="btn-primary w-full py-4 text-lg font-bold flex items-center justify-center space-x-3 shadow-lg shadow-blue-500/20"
          >
            <Send className="w-5 h-5" />
            <span>Deploy Email</span>
          </button>
        </form>
      </div>

      {/* Loading Overlay Modal */}
      {(isAnyLoading) && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-sm w-full p-10 text-center animate-in zoom-in duration-300">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
              <div className="absolute inset-4 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full animate-pulse shadow-lg"></div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Almost there...</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              {showExtendedMessage ? "Working our magic... ✨" : "Processing your request..."}
            </p>

           
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-8">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/50 rounded-2xl flex items-center justify-center mb-6">
                <Save className="w-7 h-7 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Save as Template?</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Want to reuse this layout later? Give it a name and it'll be saved to your library.
              </p>

              <div className="space-y-6">
                <div>
                  <label className="label-text">Template Name</label>
                  <input
                    type="text"
                    value={templateNameInput}
                    onChange={(e) => setTemplateNameInput(e.target.value)}
                    placeholder="e.g. December Newsletter"
                    className="input-field"
                    autoFocus
                  />
                </div>

                <div className="flex flex-col gap-3">
                  {templateNameInput.trim() ? (
                    <button
                      onClick={() => handleSendEmail(true, templateNameInput)}
                      className="btn-primary w-full py-3 flex items-center justify-center"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save & Send
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSendEmail(false)}
                      className="btn-primary w-full py-3 flex items-center justify-center bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Just Send
                    </button>
                  )}

                  <button
                    onClick={() => setShowSaveTemplateModal(false)}
                    className="btn-secondary w-full py-3 border-transparent"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailForm;
